/**
 * NileWishlist - Global Wishlist Module
 * 
 * Handles wishlist functionality with:
 * - API calls for logged-in users
 * - localStorage for guest users
 * - Custom events for UI updates
 * 
 * Usage:
 * - Add: NileWishlist.add(item) or click element with [data-wishlist-add]
 * - Remove: NileWishlist.remove(variantId)
 * - Get all: NileWishlist.getAll()
 * - Check: NileWishlist.isInWishlist(variantId)
 * - Count: NileWishlist.getCount()
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'nile_wishlist';
  const API_BASE = '/apps/wishlist';

  // Check if customer is logged in
  function isLoggedIn() {
    return !!(window.NileCustomer && window.NileCustomer.id);
  }

  // Get customer ID for API calls
  function getCustomerId() {
    return window.NileCustomer?.id || null;
  }

  // ==================== localStorage Operations ====================

  // In-memory cache for wishlist items
  let wishlistCache = null;

  function getLocalWishlist() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  function saveLocalWishlist(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      wishlistCache = items;
    } catch (e) {
      // Silent fail
    }
  }

  // Extract numeric ID from GID format (e.g., "gid://shopify/ProductVariant/123" -> "123")
  function toNumericId(gid) {
    if (!gid) return '';
    return gid.includes('gid://') ? gid.split('/').pop() : String(gid);
  }

  function addToLocal(item) {
    const items = getLocalWishlist();
    const numericVariantId = toNumericId(item.variantId);
    const existingIndex = items.findIndex(i => toNumericId(i.variantId) === numericVariantId);

    if (existingIndex === -1) {
      const newItem = {
        ...item,
        productId: toNumericId(item.productId),
        variantId: numericVariantId,
        addedAt: new Date().toISOString()
      };
      items.push(newItem);
      saveLocalWishlist(items);
      return true;
    }
    return false;
  }

  function removeFromLocal(variantId) {
    const items = getLocalWishlist();
    const numericId = toNumericId(variantId);
    const filteredItems = items.filter(i => toNumericId(i.variantId) !== numericId);

    if (filteredItems.length !== items.length) {
      saveLocalWishlist(filteredItems);
      return true;
    }
    return false;
  }

  function isInLocal(variantId) {
    const items = wishlistCache || getLocalWishlist();
    const numericId = toNumericId(variantId);
    return items.some(i => toNumericId(i.variantId) === numericId);
  }

  function clearLocal() {
    saveLocalWishlist([]);
    wishlistCache = [];
  }

  // Load wishlist into cache on init
  function loadWishlistCache() {
    if (wishlistCache === null) {
      wishlistCache = getLocalWishlist();
    }
    return wishlistCache;
  }

  // ==================== API Operations ====================

  async function apiRequest(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const text = await response.text();
      return text ? JSON.parse(text) : null;
    } catch (e) {
      throw e;
    }
  }

  async function getFromAPI() {
    try {
      const data = await apiRequest('', { method: 'GET' });
      return data?.items || data || [];
    } catch (e) {
      return [];
    }
  }

  async function addToAPI(item) {
    try {
      // Convert GIDs to numeric IDs
      const payload = {
        ...item,
        productId: toNumericId(item.productId),
        variantId: toNumericId(item.variantId)
      };

      await apiRequest('', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      return true;
    } catch (e) {
      return false;
    }
  }

  async function removeFromAPI(variantId) {
    try {
      const id = toNumericId(variantId);
      await apiRequest(`/${id}`, { method: 'DELETE' });
      return true;
    } catch (e) {
      return false;
    }
  }

  async function clearAPI() {
    try {
      await apiRequest('', { method: 'DELETE' });
      return true;
    } catch (e) {
      return false;
    }
  }

  // ==================== Event Dispatching ====================

  function dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      detail,
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  // ==================== Notification Banner ====================

  // Translations for notification messages
  const translations = {
    de: {
      added: 'Das Produkt wurde zu der Wunschliste hinzugefügt.',
      removed: 'Produkt erfolgreich von der Wunschliste entfernt.'
    },
    en: {
      added: 'The product has been added to your wish list.',
      removed: 'Product successfully removed from your wish list.'
    },
    fr: {
      added: 'Le produit a été ajouté à la liste de souhaits.',
      removed: 'Produit supprimé avec succès de la liste de souhaits.'
    },
    it: {
      added: 'Il prodotto è stato aggiunto alla lista dei desideri.',
      removed: 'Prodotto rimosso con successo dalla lista dei desideri.'
    }
  };

  // Get current language from Shopify
  function getCurrentLanguage() {
    return window.Shopify?.locale || 'de';
  }

  // Get translated message
  function getTranslation(key) {
    const lang = getCurrentLanguage();
    return translations[lang]?.[key] || translations.de[key];
  }

  // Notification counter for unique IDs
  let notificationCounter = 0;
  let dismissalTimeout = null;
  let isDismissing = false;

  function dismissOldestNotification() {
    if (isDismissing) return;

    const notifications = document.querySelectorAll('.nile-wishlist-notification');
    if (notifications.length === 0) {
      isDismissing = false;
      return;
    }

    isDismissing = true;

    // Get the oldest notification (highest bottom value)
    let oldestNotification = notifications[0];
    let maxBottom = parseInt(oldestNotification.style.bottom || '10');

    notifications.forEach(notif => {
      const bottom = parseInt(notif.style.bottom || '10');
      if (bottom > maxBottom) {
        maxBottom = bottom;
        oldestNotification = notif;
      }
    });

    // Hide the oldest notification
    oldestNotification.classList.add('nile-wishlist-notification--hiding');
    setTimeout(() => {
      if (oldestNotification.parentElement) {
        oldestNotification.remove();
      }

      // Update positions of remaining notifications
      const remaining = document.querySelectorAll('.nile-wishlist-notification');
      remaining.forEach((notif, index) => {
        const newBottom = index * 12 + 10;
        notif.style.bottom = `${newBottom}px`;
        notif.style.zIndex = 10000 - index;
      });

      isDismissing = false;

      // Continue dismissing if there are more notifications
      if (remaining.length > 0) {
        setTimeout(dismissOldestNotification, 200); // Wait 200ms between dismissals
      }
    }, 150);
  }

  function startDismissalSequence() {
    // Clear any existing dismissal timeout
    if (dismissalTimeout) {
      clearTimeout(dismissalTimeout);
    }

    // Wait 1 second after the last notification, then start dismissing
    dismissalTimeout = setTimeout(() => {
      dismissOldestNotification();
    }, 3000);
  }

  function showNotification(message, type = 'success') {
    // Get all existing notifications
    const existingNotifications = document.querySelectorAll('.nile-wishlist-notification');

    // Update bottom position of existing notifications to stack them
    existingNotifications.forEach((notif, index) => {
      const newBottom = (index + 1) * 12 + 10; // 12px spacing between notifications
      notif.style.bottom = `${newBottom}px`;
      notif.style.zIndex = 10000 - (index + 1);
    });

    // Create notification element
    notificationCounter++;
    const notification = document.createElement('div');
    notification.className = `nile-wishlist-notification nile-wishlist-notification--${type}`;
    notification.setAttribute('data-notification-id', notificationCounter);
    notification.innerHTML = `
      <div class="nile-wishlist-notification__content">
        <span class="nile-wishlist-notification__message">${message}</span>
        <button class="nile-wishlist-notification__close" aria-label="Close notification">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;

    // Add styles if not already added
    if (!document.getElementById('nile-wishlist-notification-styles')) {
      const style = document.createElement('style');
      style.id = 'nile-wishlist-notification-styles';
      style.textContent = `
        .nile-wishlist-notification {
          position: fixed;
          bottom: 10px;
          right: 10px;
          width: 400px;
          background: #fff;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          padding: 12px 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 10000;
          animation: nile-wishlist-slide-in 0.3s ease-out;
          transition: bottom 0.3s ease-out;
          max-height: 80px;
          overflow: hidden;
        }

        .nile-wishlist-notification__content {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .nile-wishlist-notification__message {
          font-size: 14px;
          line-height: 1.5;
          color: #333;
          flex: 1;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .nile-wishlist-notification__close {
          flex-shrink: 0;
          background: none;
          border: none;
          padding: 4px;
          cursor: pointer;
          color: #999;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s ease;
          margin-left: 8px;
        }

        .nile-wishlist-notification__close:hover {
          color: #333;
        }

        @keyframes nile-wishlist-slide-in {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes nile-wishlist-slide-out {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(400px);
            opacity: 0;
          }
        }

        .nile-wishlist-notification--hiding {
          animation: nile-wishlist-slide-out 0.3s ease-out forwards;
        }

        @media (max-width: 768px) {
          .nile-wishlist-notification {
            width: 80% !important;
            right: 10px;
            bottom: 10px;
            white-space: normal;
          }
        }
      `;
      document.head.appendChild(style);
    }

    // Add to DOM
    document.body.appendChild(notification);

    // Close button handler
    const closeButton = notification.querySelector('.nile-wishlist-notification__close');
    const hideNotification = () => {
      notification.classList.add('nile-wishlist-notification--hiding');
      setTimeout(() => {
        if (notification.parentElement) {
          notification.remove();
        }

        // Update positions of remaining notifications
        const remaining = document.querySelectorAll('.nile-wishlist-notification');
        remaining.forEach((notif, index) => {
          const newBottom = index * 12 + 10;
          notif.style.bottom = `${newBottom}px`;
          notif.style.zIndex = 10000 - index;
        });
      }, 300);
    };

    closeButton.addEventListener('click', hideNotification);

    // Start the dismissal sequence after 1.5 seconds
    startDismissalSequence();
  }

  // ==================== Public API ====================

  const NileWishlist = {
    /**
     * Add item to wishlist
     * @param {Object} item - Wishlist item data
     * @returns {Promise<boolean>} Success status
     */
    async add(item) {
      if (!item.variantId) {
        return false;
      }

      let success = false;

      if (isLoggedIn()) {
        success = await addToAPI(item);
      } else {
        success = addToLocal(item);
      }

      if (success) {
        dispatchEvent('wishlist:added', { item });
        dispatchEvent('wishlist:updated', { action: 'add', item });

        // Show notification
        showNotification(getTranslation('added'), 'success');
      }

      return success;
    },

    /**
     * Remove item from wishlist
     * @param {string} variantId - Variant ID to remove
     * @returns {Promise<boolean>} Success status
     */
    async remove(variantId) {
      if (!variantId) {
        return false;
      }

      let success = false;

      if (isLoggedIn()) {
        success = await removeFromAPI(variantId);
      } else {
        success = removeFromLocal(variantId);
      }

      if (success) {
        dispatchEvent('wishlist:removed', { variantId });
        dispatchEvent('wishlist:updated', { action: 'remove', variantId });

        // Show notification
        showNotification(getTranslation('removed'), 'success');
      }

      return success;
    },

    /**
     * Toggle item in wishlist (add if not exists, remove if exists)
     * @param {Object} item - Wishlist item data
     * @returns {Promise<{action: string, success: boolean}>}
     */
    async toggle(item) {
      const inWishlist = await this.isInWishlist(item.variantId);

      if (inWishlist) {
        const success = await this.remove(item.variantId);
        return { action: 'remove', success };
      } else {
        const success = await this.add(item);
        return { action: 'add', success };
      }
    },

    /**
     * Get all wishlist items
     * @returns {Promise<Array>} Array of wishlist items
     */
    async getAll() {
      if (isLoggedIn()) {
        return await getFromAPI();
      } else {
        return wishlistCache !== null ? wishlistCache : loadWishlistCache();
      }
    },

    /**
     * Check if item is in wishlist
     * @param {string} variantId - Variant ID to check
     * @returns {Promise<boolean>}
     */
    async isInWishlist(variantId) {
      if (!variantId) return false;

      const items = await this.getAll();
      const numericId = toNumericId(variantId);
      return items.some(i => toNumericId(i.variantId) === numericId);
    },

    /**
     * Get wishlist count
     * @returns {Promise<number>}
     */
    async getCount() {
      const items = await this.getAll();
      return items.length;
    },

    /**
     * Clear all wishlist items
     * @returns {Promise<boolean>}
     */
    async clear() {
      let success = false;

      if (isLoggedIn()) {
        success = await clearAPI();
      } else {
        clearLocal();
        success = true;
      }

      if (success) {
        dispatchEvent('wishlist:cleared');
        dispatchEvent('wishlist:updated', { action: 'clear' });
      }

      return success;
    },

    /**
     * Sync localStorage wishlist to API (call after login)
     * @returns {Promise<boolean>}
     */
    async syncToAPI() {
      if (!isLoggedIn()) {
        return false;
      }

      const localItems = getLocalWishlist();
      if (localItems.length === 0) return true;

      let allSuccess = true;
      for (const item of localItems) {
        const success = await addToAPI(item);
        if (!success) allSuccess = false;
      }

      if (allSuccess) {
        clearLocal();
        dispatchEvent('wishlist:synced');
        dispatchEvent('wishlist:updated', { action: 'sync' });
      }

      return allSuccess;
    },

    /**
     * Check if user is logged in
     * @returns {boolean}
     */
    isLoggedIn() {
      return isLoggedIn();
    },

    /**
     * Get wishlist items for a specific customer ID (for shared wishlist view)
     * @param {string} customerId - The customer ID to fetch wishlist for
     * @returns {Promise<Array>} Array of wishlist items
     */
    async getByCustomerId(customerId) {
      if (!customerId) {
        return [];
      }

      try {
        const data = await apiRequest(`?customerId=${customerId}`, { method: 'GET' });
        return data?.items || data || [];
      } catch (e) {
        console.error('Error fetching wishlist for customer:', e);
        return [];
      }
    },

    /**
     * Initialize wishlist buttons on the page
     * Call this after dynamic content is loaded
     */
    initButtons() {
      initClickHandlers();
      updateAllButtonStates();
    }
  };

  // ==================== DOM Interaction ====================

  /**
   * Extract item data from button element
   */
  function getItemFromButton(button) {
    return {
      productId: button.dataset.productId || '',
      variantId: button.dataset.variantId || '',
      productTitle: button.dataset.productTitle || '',
      variantTitle: button.dataset.variantTitle || '',
      price: button.dataset.price || '',
      compareAtPrice: button.dataset.compareAtPrice || '',
      imageUrl: button.dataset.imageUrl || '',
      productHandle: button.dataset.productHandle || ''
    };
  }

  /**
   * Update button visual state
   */
  function updateButtonState(button, isInWishlist) {
    if (isInWishlist) {
      button.classList.add('is-in-wishlist');
      button.setAttribute('aria-pressed', 'true');
    } else {
      button.classList.remove('is-in-wishlist');
      button.setAttribute('aria-pressed', 'false');
    }
  }

  /**
   * Initialize click handlers using event delegation
   */
  function initClickHandlers() {
    if (window._wishlistDelegationInit) return;
    window._wishlistDelegationInit = true;

    document.addEventListener('click', async function (event) {
      const button = event.target.closest('[data-wishlist-add]');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      const item = getItemFromButton(button);

      if (!item.variantId) {
        return;
      }

      button.classList.add('is-loading');
      button.disabled = true;

      try {
        const result = await NileWishlist.toggle(item);
        updateButtonState(button, result.action === 'add');
      } catch (e) {
        // Silent fail
      } finally {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
    });
  }

  /**
   * Update visual state of all wishlist buttons
   */
  async function updateAllButtonStates() {
    const buttons = document.querySelectorAll('[data-wishlist-add]');
    if (buttons.length === 0) return;

    const items = await NileWishlist.getAll();
    const variantIds = new Set(items.map(i => toNumericId(i.variantId)));

    buttons.forEach(button => {
      const variantId = toNumericId(button.dataset.variantId);
      const isInWishlist = variantIds.has(variantId);
      updateButtonState(button, isInWishlist);
    });
  }

  /**
   * Update header wishlist count
   */
  async function updateHeaderCount() {
    const countElements = document.querySelectorAll('.wishlist-header-count-top, .aico-wishlist-header-count');
    const count = await NileWishlist.getCount();

    countElements.forEach(el => {
      el.textContent = count;
      el.dataset.totalwishlistitems = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  // ==================== Initialization ====================

  async function init() {
    loadWishlistCache();
    initClickHandlers();

    // Update button states with retry for timing issues
    await updateAllButtonStates();
    updateHeaderCount();

    // Retry after a short delay to catch any late-rendered buttons
    setTimeout(() => {
      updateAllButtonStates();
    }, 500);

    document.addEventListener('wishlist:updated', () => {
      updateHeaderCount();
      updateAllButtonStates();
    });

    document.addEventListener('shopify:section:load', () => {
      updateAllButtonStates();
    });

    observeNewButtons();
  }

  /**
   * Watch for new wishlist buttons added to DOM
   */
  function observeNewButtons() {
    const observer = new MutationObserver((mutations) => {
      let hasNewButtons = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches && node.matches('[data-wishlist-add]')) {
              hasNewButtons = true;
            } else if (node.querySelector && node.querySelector('[data-wishlist-add]')) {
              hasNewButtons = true;
            }
          }
        });
      });

      if (hasNewButtons) {
        updateAllButtonStates();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Expose to global scope
  window.NileWishlist = NileWishlist;

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
